import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import {
  requestOtp,
  confirmDeletion,
  getDeletionStatus,
  cancelDeletion,
} from "../../services/accountDeletionService";
import "./accountDeletion.css";

const ACTIVE_STATUSES = ["PENDING", "OTP_VERIFIED", "SCHEDULED"];

const STATUS_META = {
  PENDING:      { label: "Pending",       color: "#EA580C" },
  OTP_VERIFIED: { label: "OTP Verified",  color: "#2563EB" },
  SCHEDULED:    { label: "Scheduled",     color: "#DC2626" },
  CANCELLED:    { label: "Cancelled",     color: "#6B7280" },
  DELETED:      { label: "Deleted",       color: "#6B7280" },
};

const mapError = (msg = "") => {
  const m = msg.toLowerCase();
  if (m.includes("invalid") && m.includes("otp")) return "Invalid OTP. Please check and try again.";
  if (m.includes("expired"))                        return "OTP has expired. Please request a new one.";
  if (m.includes("too many") || m.includes("rate")) return "Too many attempts. Please wait and try again.";
  if (m.includes("already") && m.includes("cancel")) return "Deletion request has already been cancelled.";
  if (m.includes("not found"))                      return "No active deletion request found.";
  return msg || "Something went wrong. Please try again.";
};

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : null;

// ─── Info Screen ────────────────────────────────────────────────────────────
const InfoScreen = ({ onProceed }) => (
  <div className="ad-screen">
    <div className="ad-warning-banner">
      ⚠️ Account deletion is permanent and cannot be undone.
    </div>
    <div className="ad-card">
      <h3 className="ad-section-title">What will be deleted</h3>
      <ul className="ad-consequences">
        <li>All personal data (name, email, profile picture) permanently removed after 30 days</li>
        <li>Session bookings and history deleted</li>
        <li>Active subscription plans cancelled with no refund</li>
        <li>Referral code and earned rewards lost</li>
        <li>Support tickets and conversation history removed</li>
        <li>Account cannot be recovered after deletion</li>
        <li>A verification OTP will be sent to your registered email to confirm</li>
      </ul>
    </div>
    <button className="ad-btn-danger" onClick={onProceed}>
      Delete My Account
    </button>
  </div>
);

// ─── Confirm Dialog ──────────────────────────────────────────────────────────
const ConfirmDialog = ({ onConfirm, onCancel, loading }) => (
  <div className="ad-dialog-overlay">
    <div className="ad-dialog">
      <h3>Are you sure?</h3>
      <p>
        This action will send a verification OTP to your registered email. Once confirmed,
        your account will be scheduled for permanent deletion.
      </p>
      <div className="ad-dialog-actions">
        <button className="ad-btn-danger" onClick={onConfirm} disabled={loading}>
          {loading ? "Sending…" : "Yes, Send OTP"}
        </button>
        <button className="ad-btn-secondary" onClick={onCancel} disabled={loading}>
          Cancel
        </button>
      </div>
    </div>
  </div>
);

// ─── OTP Screen ──────────────────────────────────────────────────────────────
const OtpScreen = ({ email, onConfirm, onCancel, loading, error }) => {
  const [otp, setOtp] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMsg, setResendMsg] = useState("");

  const handleResend = async () => {
    setResendLoading(true);
    setResendMsg("");
    const res = await requestOtp();
    setResendLoading(false);
    setResendMsg(res.status === 200 ? "OTP resent successfully." : mapError(res.message));
  };

  return (
    <div className="ad-screen">
      <div className="ad-card">
        <h3 className="ad-otp-heading">Enter Verification OTP</h3>
        <p className="ad-otp-sub">
          We've sent a verification code to your registered email. Enter it below to confirm
          account deletion.
        </p>
        {email && <div className="ad-otp-email">{email}</div>}
        <input
          className="ad-otp-input"
          type="tel"
          inputMode="numeric"
          maxLength={6}
          placeholder="000000"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
        />
        {error && <p className="ad-error">{error}</p>}
        {resendMsg && <p className="ad-resend-msg">{resendMsg}</p>}
        <button
          className="ad-btn-danger"
          onClick={() => onConfirm(otp)}
          disabled={loading || otp.length < 4}
        >
          {loading ? "Verifying…" : "Confirm Deletion"}
        </button>
        <button className="ad-btn-link" onClick={handleResend} disabled={resendLoading}>
          {resendLoading ? "Resending…" : "Resend OTP"}
        </button>
        <button className="ad-btn-secondary" onClick={onCancel} disabled={loading}>
          Cancel
        </button>
      </div>
    </div>
  );
};

// ─── Status Screen ───────────────────────────────────────────────────────────
const StatusScreen = ({ status, onCancel, cancelLoading, cancelError }) => {
  const meta = STATUS_META[status.status] || { label: status.status, color: "#6B7280" };

  const timeline = [
    { label: "OTP Requested",      value: fmt(status.requestedAt) },
    { label: "OTP Verified",       value: fmt(status.otpVerifiedAt) },
    { label: "Scheduled Deletion", value: fmt(status.scheduledDeletionAt) },
    ...(status.finalReminderSent ? [{ label: "Final Reminder Sent", value: fmt(status.finalReminderSentAt) }] : []),
  ].filter((t) => t.value);

  return (
    <div className="ad-screen">
      <div className="ad-status-banner">
        🔔 <strong>Deletion In Progress</strong> — Your account is scheduled for deletion.
        You can cancel anytime before it's processed.
      </div>

      <div className="ad-card">
        <div className="ad-status-row">
          <span className="ad-status-label">Status</span>
          <span className="ad-status-badge" style={{ background: meta.color }}>
            {meta.label}
          </span>
        </div>

        {timeline.length > 0 && (
          <div className="ad-timeline">
            {timeline.map((t) => (
              <div className="ad-timeline-item" key={t.label}>
                <div className="ad-timeline-dot" />
                <div>
                  <div className="ad-timeline-event">{t.label}</div>
                  <div className="ad-timeline-time">{t.value}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {status.adminNote && (
          <div className="ad-admin-note">
            <span className="ad-admin-note-label">Note from Admin</span>
            <p>{status.adminNote}</p>
          </div>
        )}
      </div>

      {cancelError && <p className="ad-error">{cancelError}</p>}
      <button className="ad-btn-teal" onClick={onCancel} disabled={cancelLoading}>
        {cancelLoading ? "Cancelling…" : "Cancel Deletion Request"}
      </button>
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────
const AccountDeletionPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [screen, setScreen] = useState("loading"); // loading | info | otp | status
  const [showDialog, setShowDialog] = useState(false);
  const [statusData, setStatusData] = useState(null);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState("");

  useEffect(() => {
    const load = async () => {
      const res = await getDeletionStatus();
      if (res.status === 200 && res.data?.status && ACTIVE_STATUSES.includes(res.data.status)) {
        setStatusData(res.data);
        setScreen("status");
      } else {
        setScreen("info");
      }
    };
    load();
  }, []);

  const handleSendOtp = async () => {
    setOtpLoading(true);
    const res = await requestOtp();
    setOtpLoading(false);
    if (res.status === 200) {
      setShowDialog(false);
      setScreen("otp");
    } else {
      setOtpError(mapError(res.message));
    }
  };

  const handleConfirm = async (otp) => {
    setConfirmLoading(true);
    setOtpError("");
    const res = await confirmDeletion(otp);
    setConfirmLoading(false);
    if (res.status === 200) {
      const statusRes = await getDeletionStatus();
      if (statusRes.status === 200) setStatusData(statusRes.data);
      setScreen("status");
    } else {
      setOtpError(mapError(res.message));
    }
  };

  const handleCancel = async () => {
    setCancelLoading(true);
    setCancelError("");
    const res = await cancelDeletion();
    setCancelLoading(false);
    if (res.status === 200) {
      setStatusData(null);
      setScreen("info");
    } else {
      setCancelError(mapError(res.message));
    }
  };

  return (
    <div className="ad-page">
      <div className="ad-navbar">
        <button className="ad-back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft />
        </button>
        <span className="ad-navbar-title">Delete Account</span>
      </div>

      <div className="ad-content">
        {screen === "loading" && (
          <div className="ad-loading">Checking account status…</div>
        )}
        {screen === "info" && (
          <InfoScreen onProceed={() => setShowDialog(true)} />
        )}
        {screen === "otp" && (
          <OtpScreen
            email={user?.email}
            onConfirm={handleConfirm}
            onCancel={() => setScreen("info")}
            loading={confirmLoading}
            error={otpError}
          />
        )}
        {screen === "status" && statusData && (
          <StatusScreen
            status={statusData}
            onCancel={handleCancel}
            cancelLoading={cancelLoading}
            cancelError={cancelError}
          />
        )}
      </div>

      {showDialog && (
        <ConfirmDialog
          onConfirm={handleSendOtp}
          onCancel={() => { setShowDialog(false); setOtpError(""); }}
          loading={otpLoading}
          error={otpError}
        />
      )}
    </div>
  );
};

export default AccountDeletionPage;
